import React, { useState, useRef, useEffect } from "react";
import { ChevronLeft, X, AlertCircle, CheckCircle2 } from "lucide-react";
import { supabase } from "./supabaseClient";
import Stepper, { Step, StepperHandle } from "./Stepper";
import { ThemeToggleSwitch } from "./App";
import CountyAutocompleteInput from "./CountyAutocompleteInput";
import { normalizeCountyName } from "./KenyaCounties";

// --- DESCRIPTION GUARDRAILS ---
// Sellers must not be able to quote a different price inside the free-text
// description than what they actually set for the listing — a buyer who
// only reads the description could get misled (e.g. item is set at 450
// KES but the text says "goes for 600"). Two layers:
//  1. Digits are stripped live as the seller types (can't type a number
//     in the box at all), so no price can be spelled out numerically.
//  2. Currency/price wording is blocked at submit time, since these are
//     whole words/phrases that only make sense checked against the full
//     text, not strippable character-by-character like digits.
// Mirrored in ASD (Create Listing form) — keep both lists in sync if this
// changes.
export const DESCRIPTION_MAX_LENGTH = 400;
export const BANNED_DESCRIPTION_KEYWORDS = [
  "sh.",
  "kshs.",
  "ksh.",
  "shillings",
  "shilling",
  "kenya shillings",
  "kenyan shillings",
  "kes",
  "ksh",
  "kshs",
  "shs",
  "amount",
  "money",
  "price",
  "cost",
  "cash",
  "bob",
  "/=",
];

// Strips any digit as the seller types — used directly in a textarea's
// onChange so numbers never make it into the field at all.
export function stripDigits(value: string) {
  return value.replace(/[0-9]/g, "");
}

// Returns the first banned keyword found in the text (case-insensitive,
// simple substring match — matches how the words could actually appear),
// or null if the description is clean.
export function findBannedDescriptionKeyword(value: string) {
  const lower = value.toLowerCase();
  return (
    BANNED_DESCRIPTION_KEYWORDS.find((word) => lower.includes(word)) || null
  );
}

// LEGACY — no longer used by the seller flow. Young Professionals used to type
// "Area, County" into one Location field. The county is now its own required
// field (see CountyAutocompleteInput), so Location is just the area. Kept
// exported only in case another file still imports it.
export function isValidAreaCountyLocation(value: string) {
  const parts = value.split(",");
  return parts.length === 2 && parts.every((part) => part.trim().length > 0);
}

// Kenyan mobile numbers all follow +254 + 9 digits, but the leading digit
// after 254 varies (7 for Safaricom/Airtel/etc., 1 for the newer 010/011
// ranges) — so this can't hardcode "+2547...", just the country code
// itself plus the right total digit count. Whitespace inside the number
// (e.g. "+254 712 345 678") is tolerated and stripped before checking.
export function isValidKenyanPhoneNumber(value: string) {
  const compact = value.replace(/\s+/g, "");
  return /^\+254[17]\d{8}$/.test(compact);
}

// --- PHONE INPUT -----------------------------------------------------------
// The field is pre-filled with "+254" and the seller only types the rest.
// Whatever they type or paste ("0717550027", "254717550027", "+254 717 550
// 027", an autofill) is folded back into "+254" + up to 9 digits, and the
// "+254" itself can't be deleted.
// The approval email (with the seller's verification code) goes to this address,
// so a typo here means a seller who can never get in. Light check only —
// the real test is whether the email arrives.
export function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

export const PHONE_PREFIX = "+254";
export function normalizePhoneInput(raw: string) {
  // Backspacing into the prefix ("", "+", "+2", "+25") just leaves the prefix.
  if (PHONE_PREFIX.startsWith(raw.trim())) return PHONE_PREFIX;
  let digits = raw.replace(/\D/g, "");
  if (digits.startsWith("254")) digits = digits.slice(3);
  digits = digits.replace(/^0+/, ""); // 0717... -> 717...
  return PHONE_PREFIX + digits.slice(0, 9);
}

// --- PHOTO UPLOAD HELPERS --------------------------------------------------
// Phone cameras produce 3-5MB+ photos. Pushing several of those over mobile
// data in one go is the most common reason an upload dies, so every photo is
// shrunk (longest side 1600px, JPEG) before it's sent. Falls back to the
// original file if anything about that fails, and never makes a file bigger.
async function compressImage(
  file: File,
  maxDim = 1600,
  quality = 0.82
): Promise<File> {
  try {
    if (typeof createImageBitmap !== "function") return file;
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size <= 600 * 1024) {
      bitmap.close?.();
      return file; // already small
    }
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close?.();
      return file;
    }
    ctx.fillStyle = "#ffffff"; // PNGs with transparency would otherwise go black
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality)
    );
    if (!blob || blob.size >= file.size) return file;
    const base = file.name.replace(/\.[^.]+$/, "") || "photo";
    return new File([blob], `${base}.jpg`, { type: "image/jpeg" });
  } catch {
    return file;
  }
}

function extensionForMime(mime: string) {
  if (mime === "image/png") return "png";
  if (mime === "image/webp") return "webp";
  return "jpg";
}

// One photo, retried a couple of times if it's just a flaky connection.
// Auth / permission / size errors are not retried — they won't fix themselves.
async function uploadPhotoWithRetry(path: string, file: File, attempts = 3) {
  let lastError: any;
  for (let i = 0; i < attempts; i++) {
    // A stalled mobile connection can hang forever; give each attempt 60s.
    const result: { error: any } = await Promise.race([
      supabase.storage.from("product-images").upload(path, file, {
        contentType: file.type || "image/jpeg",
        cacheControl: "31536000",
        upsert: false,
      }),
      new Promise<{ error: any }>((resolve) =>
        setTimeout(
          () => resolve({ error: new Error("Upload timed out") }),
          60_000
        )
      ),
    ]);
    const error = result.error;
    if (!error) return;
    lastError = error;
    const msg = String((error as any).message || "").toLowerCase();
    const status = Number((error as any).statusCode ?? (error as any).status);
    const transient =
      /failed to fetch|network|load failed|timeout|timed out|aborted/.test(msg) ||
      status >= 500;
    if (!transient || i === attempts - 1) break;
    await new Promise((r) => setTimeout(r, 800 * (i + 1)));
  }
  throw lastError;
}

// Turn a storage error into something the seller can act on.
function describeUploadError(e: any): string {
  const msg = String(e?.message || e || "").toLowerCase();
  const status = Number(e?.statusCode ?? e?.status);
  if (/jwt|expired|unauthorized|not authenticated/.test(msg) || status === 401)
    return "Your session has timed out. Please refresh the page, sign back in, and submit again.";
  if (/row-level security|violates|permission|forbidden/.test(msg) || status === 403)
    return "We couldn't save your photos with this login. Please sign out, sign back in, and try again.";
  if (/too large|exceeded|payload/.test(msg) || status === 413)
    return "One of your photos is too large. Please choose smaller photos and try again.";
  if (
    /failed to fetch|network|load failed|timeout|timed out|aborted/.test(msg) ||
    (typeof navigator !== "undefined" && navigator.onLine === false)
  )
    return "We couldn't reach the server. Check your connection and try again.";
  return "Failed to upload product images. Please try again.";
}

// Link to the "Choosing Your Category & Listing Type" reference PDF —
// shown on Step 3 so applicants can check what each subcategory covers
// and whether it's Physical Product, Service, or both before picking.
// Hosted externally in Supabase Storage. Since this is a *different*
// origin than the app itself, a plain <a download> would be ignored by
// most browsers (the `download` attribute only forces a save — rather
// than navigating — for same-origin URLs) and the click would instead
// navigate the whole page away to the PDF, losing whatever the seller
// had filled in so far. `downloadGuidePdf` below works around that by
// fetching the file in the background and saving it from a same-origin
// blob: URL instead, so the click never navigates anywhere and the
// in-progress form is untouched.
const CATEGORY_GUIDE_PDF_URL =
  "https://jnoeucdmnuffzctyhomt.supabase.co/storage/v1/object/public/assets/docs/INUA%20SG.pdf";

async function downloadGuidePdf() {
  try {
    const response = await fetch(CATEGORY_GUIDE_PDF_URL);
    if (!response.ok) throw new Error("Guide fetch failed");
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = "INUA-Seller-Guide.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch {
    // If the fetch is blocked (e.g. a CORS hiccup) or the file's
    // unreachable, fall back to just opening it in a new tab rather than
    // failing silently — the current tab (and the form in it) stays put
    // either way.
    window.open(CATEGORY_GUIDE_PDF_URL, "_blank", "noopener,noreferrer");
  }
}

// The 7 broad listing categories a seller can register under. Ids/names/
// colors mirror the parent groups in App.tsx's CATEGORIES exactly, so the
// value saved here lines up with how the buyer-facing app filters and
// themes listings by category.
const CATEGORIES = {
  home_services: {
    id: "home_services",
    name: "HOME SERVICES",
    color: "#0ea5e9",
    statement: "Everyday help, sorted.",
  },
  fashion_beauty: {
    id: "fashion_beauty",
    name: "FASHION & BEAUTY",
    color: "#f97316",
    statement: "Style that speaks for itself.",
  },
  furniture_home_goods: {
    id: "furniture_home_goods",
    name: "FURNITURE & HOME GOODS",
    color: "#8B4513",
    statement: "Build your campus space.",
  },
  food_drinks: {
    id: "food_drinks",
    name: "FOOD & DRINKS",
    color: "#22c55e",
    statement: "Feed the hustle.",
  },
  tech_electronics: {
    id: "tech_electronics",
    name: "TECH & ELECTRONICS",
    color: "#6366f1",
    statement: "Gadgets, fixed and flipped.",
  },
  academic_services: {
    id: "academic_services",
    name: "ACADEMIC SERVICES",
    color: "#a855f7",
    statement: "Smarter, together.",
  },
  events_creative: {
    id: "events_creative",
    name: "EVENTS & CREATIVE",
    color: "#ec4899",
    statement: "Make it memorable.",
  },
};

// Categories that only ever contain services. Picking one of these locks
// the listing type to "Service" (Physical Product is disabled).
const SERVICE_ONLY_CATEGORIES = ["home_services", "academic_services"];

// The visual step count the applicant sees. Kept at 4 so the form reads as
// a handful of short screens (identity, contact, listing basics, photos)
// rather than one long one — see the field/step map below.
const TOTAL_STEPS = 4;

// Key of every field the applicant must fill in, in the order they appear
// across the whole flow. Used two ways:
//  1. The step's own "Continue" click only checks the entries whose `step`
//     matches the step you're leaving, so you can't advance past a step
//     with something missing.
//  2. The final "Submit for review" click checks *all* of them, in order —
//     this is the "always clickable, jumps you to what's missing" behavior
//     the flow relies on instead of just disabling the button.
type FieldKey =
  | "fullName"
  | "status"
  | "institution"
  | "location"
  | "county"
  | "email"
  | "phone"
  | "category"
  | "listingType"
  | "photos"
  | "description"
  | "agreed";

const FIELD_MESSAGES: Record<FieldKey, string> = {
  fullName: "Please enter your full legal name.",
  status: "Please let us know your status.",
  institution: "Please enter your institution or campus.",
  location: "Please enter your location (e.g., Donholm).",
  county: "Please choose your county from the list.",
  email: "Please enter a valid email address.",
  phone:
    "Please enter your full phone number after +254 (e.g. 712345678).",
  category: "Please select a category.",
  listingType: "Please choose a listing type.",
  photos: "Please upload 2-4 portfolio photos.",
  description: "Please describe what you're selling.",
  agreed: "Please confirm the uploaded photos are your own original work.",
};

export default function SellerFlow({
  navigateTo,
  showSellerLogin,
  setShowSellerLogin,
  setListings,
}: any) {
  const [fullName, setFullName] = useState("");
  const [sellerEmail, setSellerEmail] = useState("");
  const [sellerPhone, setSellerPhone] = useState(PHONE_PREFIX);
  const [listingType, setListingType] = useState("");
  const [selectedCat, setSelectedCat] = useState("");
  // Home & Academic Services can only be listed as services.
  const productLocked = SERVICE_ONLY_CATEGORIES.includes(selectedCat);
  const [institution, setInstitution] = useState("");
  // Status the seller picks in Step 1: "Current Student", "Recent Graduate",
  // or "Young Professional". Drives whether we show the Institution field
  // or the Location field below, and which of the two separate
  // `university` / `location` columns gets saved on submit.
  const [sellerStatus, setSellerStatus] = useState("");
  const [location, setLocation] = useState("");
  // One of the 47 county names (exact), or "" until the seller picks one.
  // Required for every seller type; powers the buyer-side county filter.
  const [county, setCounty] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");

  // Which of the Stepper's steps is currently showing, mirrored from its
  // onStepChange so the header's back chevron and the submit-time
  // validation jump both know where the applicant is.
  const [currentStep, setCurrentStep] = useState(1);
  const stepperRef = useRef<StepperHandle>(null);

  // Key of whichever field just failed validation, so it can be given a
  // temporary highlight. Cleared automatically after a couple of seconds.
  const [invalidField, setInvalidField] = useState<FieldKey | null>(null);
  const invalidTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // App-wide toast replacing native browser alert() popups — same visual
  // language as the rest of the app (e.g. ASD's "Contact card required"
  // toast). Auto-dismisses on its own.
  const [appToast, setAppToast] = useState<{
    title: string;
    message: string;
    variant?: "warning" | "info";
    durationMs?: number;
  } | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showAppToast = (
    toast: {
      title: string;
      message: string;
      variant?: "warning" | "info";
    },
    durationMs = 6000
  ) => {
    setAppToast({ ...toast, durationMs });
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => {
      setAppToast(null);
    }, durationMs);
  };

  // If the upload is still running after a few seconds (big photos / slow
  // data), reassure the seller with a short pop-up so they don't think the
  // app froze or tap Submit again.
  useEffect(() => {
    if (!isSubmitting) return;
    const t = setTimeout(() => {
      showAppToast(
        {
          title: "This might take a while",
          message:
            "Your photos are still uploading. Please keep this page open.",
          variant: "info",
        },
        8000
      );
    }, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting]);

  const handleSellerSubmit = async () => {
    try {
      setIsSubmitting(true);
      setUploadProgress("");

      let sessionUserId = "";
      // 0. MAKE SURE THE LOGIN IS STILL VALID *BEFORE* UPLOADING.
      // Someone can sit on this form for a long time (or leave the tab in the
      // background), and an expired login makes every photo upload fail with
      // a confusing "check your connection" message.
      {
        const { data: sessionData } = await supabase.auth.getSession();
        let session = sessionData.session;
        const expiresSoon =
          !!session?.expires_at &&
          session.expires_at * 1000 < Date.now() + 60_000;
        if (!session || expiresSoon) {
          const { data: refreshed } = await supabase.auth.refreshSession();
          session = refreshed?.session ?? null;
        }
        if (!session) {
          showAppToast({
            title: "Session expired",
            message:
              "Your session has timed out. Please refresh the page, sign back in, and try again!",
          });
          setIsSubmitting(false);
          return;
        }
        sessionUserId = session.user.id;
      }

      // 1. UPLOAD SELLER PHOTOS TO YOUR SUPABASE BUCKET
      const uploadedPhotoUrls: string[] = [];
      const uploadedPaths: string[] = [];

      try {
        for (let idx = 0; idx < selectedFiles.length; idx++) {
          const original = selectedFiles[idx];
          setUploadProgress(`Uploading photo ${idx + 1} of ${selectedFiles.length}...`);
          // Shrink big phone photos first — far fewer failed uploads on mobile data.
          const file = await compressImage(original);

          // Clean, unique file name (timestamp + random). The extension comes from
          // the file TYPE, not its name — some phone pickers give names with no
          // extension or with spaces/brackets, which storage rejects as a bad key.
          const fileExt = extensionForMime(file.type);
          const uniqueId = Math.random().toString(36).substring(2, 15);
          const fileName = `${Date.now()}-${uniqueId}.${fileExt}`;
          const filePath = `listings/${fileName}`;

          // Upload into the 'product-images' bucket (retries on a flaky connection)
          await uploadPhotoWithRetry(filePath, file);
          uploadedPaths.push(filePath);

          // Grab the permanent, public URL for the uploaded asset
          const {
            data: { publicUrl },
          } = supabase.storage.from("product-images").getPublicUrl(filePath);

          uploadedPhotoUrls.push(publicUrl);
        }
      } catch (error: any) {
        console.error("Image upload failed:", error, {
          message: error?.message,
          status: error?.statusCode ?? error?.status,
          online: navigator.onLine,
          userId: sessionUserId,
        });
        // Don't leave half-uploaded photos behind in the bucket.
        if (uploadedPaths.length > 0) {
          supabase.storage
            .from("product-images")
            .remove(uploadedPaths)
            .catch(() => {});
        }
        const detail = String(error?.message || error || "").slice(0, 80);
        showAppToast({
          title: "Upload failed",
          message: `${describeUploadError(error)}${
            detail ? ` (${detail})` : ""
          }`,
        });
        setIsSubmitting(false); // 📍 Reset uploader block if files fail to save
        return; // Stop the form submission if images fail to save safely
      }

      // 2. Insert form details straight to your database table
      const activeUser = sessionUserId;

      if (!activeUser) {
        showAppToast({
          title: "Session expired",
          message:
            "Your session has timed out. Please refresh the page, sign back in, and try again!",
        });
        setIsSubmitting(false);
        return;
      }

      // Every field below has already been checked, in order, by
      // handleStepButtonClick before this function is ever called — these
      // are just a defensive last line, not the primary gate.
      if (!fullName.trim()) {
        showAppToast({
          title: "Heads up",
          message: "Please enter your full name before submitting.",
        });
        setIsSubmitting(false);
        return;
      }

      if (sellerStatus === "Young Professional" && !location.trim()) {
        showAppToast({
          title: "Heads up",
          message: "Please enter your location before submitting.",
        });
        setIsSubmitting(false);
        return;
      }

      if (!county) {
        showAppToast({
          title: "Heads up",
          message: "Please choose your county before submitting.",
        });
        setIsSubmitting(false);
        return;
      }

      if (!description.trim()) {
        showAppToast({
          title: "Heads up",
          message: "Please describe what you're selling before submitting.",
        });
        setIsSubmitting(false);
        return;
      }

      const bannedWord = findBannedDescriptionKeyword(description);
      if (bannedWord) {
        showAppToast({
          title: "Description needs an edit",
          message: `Please remove pricing language from your description ("${bannedWord}"). Prices should only be set in the listing price field, not written in the description.`,
        });
        setIsSubmitting(false);
        return;
      }

      // Store the category id (e.g. "home_services") — this is what the
      // buyer-facing app matches against when filtering listings by category
      // and resolving theme color/abbreviation. The app looks up a display
      // name from this id itself, so the id is all that needs to be saved.
      const categoryToSave =
        CATEGORIES[selectedCat as keyof typeof CATEGORIES]?.id || "general";

      // `university` and `location` are now two separate columns:
      // students/grads get `university` (their institution), young
      // professionals get `location` instead — the unused one is left
      // null rather than double-booking a single column for both. The
      // Business Contact Card prefers `location` when it's set, falling
      // back to `university`.
      const isYoungProfessional = sellerStatus === "Young Professional";
      const universityToSave = isYoungProfessional
        ? null
        : institution.trim() || "Not Specified";
      // Young professionals: "<area>, <county>" — the same shape the app has
      // always shown on the Business Contact Card. If they already typed the
      // county into the area box, don't repeat it.
      const areaTrimmed = location.trim();
      const areaHasCounty =
        areaTrimmed &&
        normalizeCountyName(areaTrimmed).includes(normalizeCountyName(county));
      const locationToSave = isYoungProfessional
        ? areaTrimmed
          ? areaHasCounty
            ? areaTrimmed
            : `${areaTrimmed}, ${county}`
          : county
        : null;

      const { error: insertError } = await supabase
        .from("seller_applications")
        .insert([
          {
            id: activeUser,
            full_name: fullName,
            email: sellerEmail.trim(),
            phone: sellerPhone || "0712345678",
            university: universityToSave,
            location: locationToSave,
            county,
            category: categoryToSave,
            listing_type: listingType || "Test Type",
            status_type: "pending",
            description: description,
            photo_urls: uploadedPhotoUrls,
          },
        ]);

      if (insertError) throw insertError;

      // NOTE: We intentionally do NOT create a listing here. A seller
      // application must be reviewed and approved (see ASD15) before it
      // becomes a real row in the "listings" table. App.tsx already
      // subscribes to "listings" in realtime, so once ASD15 approves this
      // application and inserts the real listing, it'll show up in the
      // buyer feed automatically — no local state hack needed.

      // Move the user visually to the confirmation screen!
      navigateTo("seller-success");
      setIsSubmitting(false); // 📍 Turn it off on successful completion!
    } catch (error: any) {
      console.error("Submission failed:", error.message);
      showAppToast({
        title: "Something went wrong",
        message: `Error updating application: ${error.message}`,
      });
      setIsSubmitting(false); // 📍 Turn it off if the database insert crashes!
    }
  };
  // (The verification code is no longer part of the application. Approved
  // sellers enter it later, on the code screen in SellerPortal.)
  const [agreed, setAgreed] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const filesArray = Array.from(e.target.files);

    if (selectedFiles.length + filesArray.length > 4) {
      showAppToast({
        title: "Heads up",
        message:
          "You can only upload a maximum of 4 photos for your portfolio.",
      });
      return;
    }
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

    const validFiles: File[] = [];
    const validPreviews: string[] = [];

    for (const picked of filesArray) {
      // Some Android pickers (gallery / WhatsApp / Drive) return an empty
      // MIME type; infer it from the extension so valid photos aren't
      // rejected or uploaded with a bad content type.
      let file = picked;
      if (!file.type) {
        const ext = file.name.split(".").pop()?.toLowerCase();
        const inferred =
          ext === "png" ? "image/png" : ext === "webp" ? "image/webp"
          : ext === "jpg" || ext === "jpeg" ? "image/jpeg" : "";
        if (inferred) file = new File([file], file.name, { type: inferred });
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        showAppToast({
          title: "Heads up",
          message: `"${file.name}" is not a supported format. Please upload JPG, PNG, or WebP only.`,
        });
        continue;
      }

      if (file.size > MAX_FILE_SIZE) {
        showAppToast({
          title: "Heads up",
          message: `"${file.name}" is too large! Images must be smaller than 5MB.`,
        });
        continue;
      }

      validFiles.push(file);
      validPreviews.push(URL.createObjectURL(file));
    }

    setSelectedFiles((prev: File[]) => [...prev, ...validFiles]);
    setImagePreviews((prev: string[]) => [...prev, ...validPreviews]);
  };
  // --- FIELD REFS ---
  // One ref per required field/section, used only to scroll to and focus
  // whatever the validator below flags as missing.
  const fullNameRef = useRef<HTMLInputElement>(null);
  const statusRef = useRef<HTMLSelectElement>(null);
  const institutionRef = useRef<HTMLInputElement>(null);
  const locationRef = useRef<HTMLInputElement>(null);
  const countyRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const categoryRef = useRef<HTMLDivElement>(null);
  const listingTypeRef = useRef<HTMLDivElement>(null);
  const photosRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const agreedRef = useRef<HTMLLabelElement>(null);

  // The single source of truth for "is this field filled in", walked in
  // display order. `step` says which Stepper screen the field lives on —
  // that's how the submit-time check knows which step to jump back to.
  const fieldOrder: {
    key: FieldKey;
    step: number;
    ref: React.RefObject<any>;
    isValid: () => boolean;
  }[] = [
    {
      key: "fullName",
      step: 1,
      ref: fullNameRef,
      isValid: () => fullName.trim().length > 0,
    },
    {
      key: "status",
      step: 1,
      ref: statusRef,
      isValid: () => sellerStatus.trim().length > 0,
    },
    {
      key: "institution",
      step: 1,
      ref: institutionRef,
      isValid: () =>
        sellerStatus === "Young Professional" || institution.trim().length > 0,
    },
    {
      key: "location",
      step: 1,
      ref: locationRef,
      isValid: () =>
        sellerStatus !== "Young Professional" || location.trim().length > 0,
    },
    {
      key: "county",
      step: 1,
      ref: countyRef,
      isValid: () => county.length > 0,
    },
    {
      key: "email",
      step: 2,
      ref: emailRef,
      isValid: () => isValidEmail(sellerEmail),
    },
    {
      key: "phone",
      step: 2,
      ref: phoneRef,
      isValid: () => isValidKenyanPhoneNumber(sellerPhone),
    },
    {
      key: "category",
      step: 3,
      ref: categoryRef,
      isValid: () => !!selectedCat,
    },
    {
      key: "listingType",
      step: 3,
      ref: listingTypeRef,
      isValid: () => !!listingType,
    },
    {
      key: "photos",
      step: 4,
      ref: photosRef,
      isValid: () => selectedFiles.length >= 2 && selectedFiles.length <= 4,
    },
    {
      key: "description",
      step: 4,
      ref: descriptionRef,
      isValid: () => description.trim().length > 0,
    },
    {
      key: "agreed",
      step: 4,
      ref: agreedRef,
      isValid: () => agreed,
    },
  ];

  // Highlights one field: flashes its border, scrolls it into view, moves
  // focus to it (where that's meaningful), and surfaces a toast explaining
  // what's missing. The short delay lets the Stepper's own step-change
  // animation finish before we try to scroll to something on the new step.
  const flashInvalid = (field: (typeof fieldOrder)[number]) => {
    setInvalidField(field.key);
    showAppToast({ title: "Missing info", message: FIELD_MESSAGES[field.key] });
    setTimeout(() => {
      field.ref.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "center",
      });
      (field.ref.current as HTMLElement | null)?.focus?.();
    }, 80);
    if (invalidTimeoutRef.current) clearTimeout(invalidTimeoutRef.current);
    invalidTimeoutRef.current = setTimeout(() => setInvalidField(null), 2500);
  };

  // Shared handler for every step's "Continue" button and the final
  // "Submit for review" button. On any step but the last, it only checks
  // that step's own fields before advancing. On the last step it checks
  // everything, in order — so the button is always clickable, and clicking
  // it while something's missing (anywhere in the flow) jumps back to that
  // field instead of silently doing nothing.
  const handleStepButtonClick = () => {
    const isLastStep = currentStep === TOTAL_STEPS;
    const relevantFields = isLastStep
      ? fieldOrder
      : fieldOrder.filter((f) => f.step === currentStep);
    const firstInvalid = relevantFields.find((f) => !f.isValid());

    if (firstInvalid) {
      if (firstInvalid.step !== currentStep) {
        stepperRef.current?.goToStep(firstInvalid.step);
        setTimeout(() => flashInvalid(firstInvalid), 420);
      } else {
        flashInvalid(firstInvalid);
      }
      return;
    }

    if (isLastStep) {
      handleSellerSubmit();
    } else {
      stepperRef.current?.goToStep(currentStep + 1);
    }
  };

  const invalidRing = (key: FieldKey) =>
    invalidField === key ? " border-[#FF3B5C] ring-2 ring-[#FF3B5C]/40" : "";
  const invalidSectionRing = (key: FieldKey) =>
    invalidField === key ? " ring-2 ring-[#FF3B5C]/40 -m-2 p-2" : "";

  return (
    <div className="h-screen w-full bg-[var(--bg-app)] text-[var(--text-primary)] flex flex-col max-w-md mx-auto relative overflow-hidden">
      {/* APP TOAST — replaces native alert() popups. Floats independently,
          auto-dismisses like a WhatsApp notification. */}
      {appToast && (
        <div className="fixed top-5 right-5 left-5 sm:left-auto sm:w-96 z-[200] animate-toastSlideIn">
          <style>{`
            @keyframes toastSlideIn {
              from { transform: translateY(-16px); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
            .animate-toastSlideIn { animation: toastSlideIn 0.3s ease-out; }
            @keyframes toastShrink {
              from { width: 100%; }
              to { width: 0%; }
            }
            .animate-toastShrink { animation: toastShrink 6s linear forwards; }
          `}</style>
          <div
            className="bg-[#161616] rounded-2xl shadow-2xl shadow-black/50 overflow-hidden"
            style={{
              border: `1px solid ${
                appToast.variant === "info" ? "#FFB800" : "#FF3B5C"
              }4d`,
            }}
          >
            <div className="flex items-start gap-3 p-4">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: `${
                    appToast.variant === "info" ? "#FFB800" : "#FF3B5C"
                  }26`,
                }}
              >
                <AlertCircle
                  size={18}
                  style={{
                    color: appToast.variant === "info" ? "#FFB800" : "#FF3B5C",
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[var(--text-primary)]">
                  {appToast.title}
                </p>
                <p className="text-xs text-[var(--text-secondary)] mt-0.5 leading-snug">
                  {appToast.message}
                </p>
              </div>
              <button
                onClick={() => setAppToast(null)}
                className="text-[var(--text-muted)] hover:text-[var(--text-secondary)] flex-shrink-0"
              >
                <X size={16} />
              </button>
            </div>
            <div className="h-0.5 bg-white/10">
              <div
                className="h-full animate-toastShrink"
                style={{
                  backgroundColor:
                    appToast.variant === "info" ? "#FFB800" : "#FF3B5C",
                  animationDuration: `${appToast.durationMs ?? 6000}ms`,
                }}
              />
            </div>
          </div>
        </div>
      )}

      <header className="flex items-center justify-between py-4 px-6 border-b border-[var(--border-softer)] bg-[var(--bg-app)]/90 backdrop-blur-md sticky top-0 z-20">
        <button
          onClick={() =>
            currentStep === 1
              ? navigateTo("entry")
              : stepperRef.current?.goToStep(currentStep - 1)
          }
          className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] p-2 -ml-2"
        >
          <ChevronLeft size={24} />
        </button>
        <span className="font-black tracking-widest text-lg text-transparent bg-clip-text bg-gradient-to-r from-[#1006C5] to-[#FFB800]">
          INUA
        </span>
        <ThemeToggleSwitch className="size-9 p-1.5 shrink-0" />
      </header>

      <div className="flex-1 overflow-y-auto p-6 pb-10 animate-in slide-in-from-right duration-300">
        <Stepper
          ref={stepperRef}
          initialStep={1}
          onStepChange={(s: number) => setCurrentStep(s)}
          accentColor="#FFB800"
          accentColorHover="#E6A600"
          disableStepIndicators
          nextButtonText="Continue"
          completeButtonText="Submit for review"
          nextButtonProps={{
            onClick: handleStepButtonClick,
            disabled: isSubmitting,
            type: "button",
          }}
        >
          {/* STEP 1: IDENTITY */}
          <Step>
            <h2 className="text-2xl font-bold mb-2">Create Account</h2>
            <p className="text-sm text-[var(--text-secondary)] mb-8 leading-relaxed">
              Let's verify who you are before you list your hustle.
            </p>

            <div className="space-y-4 mb-2">
              <input
                ref={fullNameRef}
                type="text"
                placeholder="Full Legal Name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className={`w-full bg-[var(--bg-container)] border border-[var(--border-soft)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FFB800] transition-colors${invalidRing(
                  "fullName"
                )}`}
              />

              <select
                ref={statusRef}
                value={sellerStatus}
                onChange={(e) => setSellerStatus(e.target.value)}
                required
                className={`w-full bg-[var(--bg-container)] border border-[var(--border-soft)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FFB800] text-[var(--text-secondary)] appearance-none transition-colors${invalidRing(
                  "status"
                )}`}
              >
                <option value="" disabled>
                  What is your status?
                </option>
                <option>Current Student</option>
                <option>Recent Graduate</option>
                <option>Young Professional</option>
              </select>

              {/* --- INSTITUTION / CAMPUS FIELD ---
                  Shown only once the applicant has told us they're a
                  student or a recent grad. */}
              {(sellerStatus === "Current Student" ||
                sellerStatus === "Recent Graduate") && (
                <div className="flex flex-col gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
                  <label className="text-sm font-medium text-[var(--text-secondary)]">
                    Institution / Campus{" "}
                    <span className="text-[#FF3B5C]">*</span>
                  </label>
                  <input
                    ref={institutionRef}
                    type="text"
                    placeholder="e.g., Kenyatta University, Eldoret National Polytechnic, etc."
                    value={institution}
                    onChange={(e) => setInstitution(e.target.value)}
                    required
                    className={`w-full bg-[var(--bg-container)] border border-[var(--border-soft)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FFB800] text-[var(--text-primary)] placeholder-gray-500 transition-colors${invalidRing(
                      "institution"
                    )}`}
                  />
                </div>
              )}

              {/* --- LOCATION FIELD ---
                  Shown only for Young Professionals, in place of the
                  Institution field above. */}
              {sellerStatus === "Young Professional" && (
                <div className="flex flex-col gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
                  <label className="text-sm font-medium text-[var(--text-secondary)]">
                    Location <span className="text-[#FF3B5C]">*</span>
                  </label>
                  <input
                    ref={locationRef}
                    type="text"
                    placeholder="e.g., Donholm, Kondele, Westlands"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    required
                    className={`w-full bg-[var(--bg-container)] border border-[var(--border-soft)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FFB800] text-[var(--text-primary)] placeholder-gray-500 transition-colors${invalidRing(
                      "location"
                    )}`}
                  />
                  <p className="text-[10px] text-[var(--text-muted)] mt-1 ml-1">
                    Your estate, area or neighbourhood.
                  </p>
                </div>
              )}

              {/* --- COUNTY FIELD ---
                  Required for every seller type. Sits right below the
                  Institution field (students / grads) or the Location field
                  (young professionals). Predictive: typing narrows the list,
                  and the seller must pick a county from it. */}
              {sellerStatus && (
                <div className="flex flex-col gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
                  <label className="text-sm font-medium text-[var(--text-secondary)]">
                    County <span className="text-[#FF3B5C]">*</span>
                  </label>
                  <CountyAutocompleteInput
                    value={county}
                    onChange={setCounty}
                    inputRef={countyRef}
                    invalidClassName={invalidRing("county")}
                  />
                  <p className="text-[10px] text-[var(--text-muted)] mt-1 ml-1">
                    Enter the county where you will be operating from, not your
                    home county.
                  </p>
                </div>
              )}
            </div>
          </Step>

          {/* STEP 2: CONTACT DETAILS */}
          <Step>
            <h2 className="text-2xl font-bold mb-2">Your Contact Details</h2>
            <p className="text-sm text-[var(--text-secondary)] mb-8 leading-relaxed">
              We'll use these to reach you about your application. Once you're
              approved, your verification code will be sent to your email.
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <input
                  ref={emailRef}
                  type="email"
                  placeholder="Email Address"
                  value={sellerEmail}
                  onChange={(e) => setSellerEmail(e.target.value)}
                  required
                  className={`w-full bg-[var(--bg-container)] border border-[var(--border-soft)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FFB800] transition-colors${invalidRing(
                    "email"
                  )}`}
                />
                <p className="text-[10px] text-[var(--text-muted)] mt-1 ml-1">
                  Use an email you check often — your approval and verification code will be sent here.
                </p>
              </div>

              <div>
                <input
                  ref={phoneRef}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={sellerPhone}
                  onChange={(e) =>
                    setSellerPhone(normalizePhoneInput(e.target.value))
                  }
                  // keep the cursor after the fixed +254
                  onFocus={(e) => {
                    const el = e.currentTarget;
                    setTimeout(() => {
                      el.setSelectionRange(el.value.length, el.value.length);
                    }, 0);
                  }}
                  required
                  className={`w-full bg-[var(--bg-container)] border border-[var(--border-soft)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FFB800] transition-colors${invalidRing(
                    "phone"
                  )}`}
                />
              </div>
            </div>
          </Step>

          {/* STEP 3: LISTING BASICS */}
          <Step>
            <h2 className="text-2xl font-bold mb-2">What Are You Selling?</h2>
            <p className="text-sm text-[var(--text-secondary)] mb-8 leading-relaxed">
              Pick the category and type that best fits your hustle.
            </p>

            <div className="flex items-center gap-3 mb-6 p-3 rounded-xl bg-[#FFB800]/10 border border-[#FFB800]/30">
              <span className="text-lg leading-none">📄</span>
              <span className="text-xs text-[var(--text-secondary)] leading-relaxed">
                <span className="font-bold text-[var(--text-primary)]">
                  Not sure which category or listing type fits?
                </span>{" "}
                <button
                  type="button"
                  onClick={downloadGuidePdf}
                  className="font-bold text-[#FFB800] underline underline-offset-2"
                >
                  Click here to understand the categories, sub categories and
                  listing types.
                </button>
              </span>
            </div>

            <div
              ref={categoryRef}
              className={`mb-6 rounded-2xl${invalidSectionRing("category")}`}
            >
              <p className="text-xs font-bold text-[var(--text-secondary)] mb-3">
                1. SELECT CATEGORY <span className="text-[#FF3B5C]">*</span>
              </p>
              <div className="grid grid-cols-2 gap-3">
                {Object.values(CATEGORIES).map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => {
                      setSelectedCat(cat.id);
                      // Service-only categories: lock the type to Service
                      // (and clear a previously chosen Physical Product).
                      if (SERVICE_ONLY_CATEGORIES.includes(cat.id)) {
                        setListingType("service");
                      }
                    }}
                    className={`p-3 rounded-xl border text-sm font-bold transition-all text-left ${
                      selectedCat === cat.id
                        ? "bg-[#FFB800]/20 border-[#FFB800] text-[var(--text-primary)]"
                        : "bg-[var(--bg-container)] border-[var(--border-soft)] text-[var(--text-secondary)] hover:border-white/30"
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div
              ref={listingTypeRef}
              className={`mb-6 rounded-2xl${invalidSectionRing("listingType")}`}
            >
              <p className="text-xs font-bold text-[var(--text-secondary)] mb-3">
                2. LISTING TYPE <span className="text-[#FF3B5C]">*</span>
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  disabled={productLocked}
                  aria-disabled={productLocked}
                  onClick={() => {
                    if (!productLocked) setListingType("product");
                  }}
                  className={`flex-1 p-3 rounded-xl border text-sm font-bold transition-all ${
                    productLocked
                      ? "opacity-40 cursor-not-allowed bg-[var(--bg-container)] border-[var(--border-soft)] text-[var(--text-secondary)]"
                      : listingType === "product"
                      ? "bg-[#FFB800]/20 border-[#FFB800] text-[var(--text-primary)]"
                      : "bg-[var(--bg-container)] border-[var(--border-soft)] text-[var(--text-secondary)]"
                  }`}
                >
                  Physical Product
                </button>
                <button
                  type="button"
                  onClick={() => setListingType("service")}
                  className={`flex-1 p-3 rounded-xl border text-sm font-bold transition-all ${
                    listingType === "service"
                      ? "bg-[#FFB800]/20 border-[#FFB800] text-[var(--text-primary)]"
                      : "bg-[var(--bg-container)] border-[var(--border-soft)] text-[var(--text-secondary)]"
                  }`}
                >
                  Service
                </button>
              </div>
              {productLocked && (
                <p className="text-[11px] text-[var(--text-muted)] mt-2 leading-relaxed">
                  {CATEGORIES[selectedCat as keyof typeof CATEGORIES]?.name} only
                  accepts services, so Physical Product isn't available.
                </p>
              )}
            </div>
          </Step>

          {/* STEP 4: PORTFOLIO & DESCRIPTION */}
          <Step>
            <h2 className="text-2xl font-bold mb-2">Show Us What You Sell</h2>
            <p className="text-sm text-[var(--text-secondary)] mb-8 leading-relaxed">
              Our team reviews every submission to protect buyers. Quality
              matters.
            </p>

            <div
              ref={photosRef}
              className={`mb-6 rounded-2xl${invalidSectionRing("photos")}`}
            >
              <p className="text-xs font-bold text-[var(--text-secondary)] mb-3">
                3. PORTFOLIO PHOTOS <span className="text-[#FF3B5C]">*</span>
              </p>
              <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C]/20 rounded-xl p-3 mb-3 flex gap-3 items-start">
                <AlertCircle
                  size={16}
                  className="text-[#FF3B5C] shrink-0 mt-0.5"
                />
                <p className="text-[11px] text-[#FF3B5C]/90 leading-relaxed">
                  Do not use photos from the internet. Our team checks every
                  submission. Sellers caught using stolen photos are permanently
                  removed.
                </p>
              </div>

              <div className="flex flex-col gap-3 my-4">
                {/* Clear guidelines text for the user */}
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-[var(--text-secondary)]">
                    Upload Portfolio Photos
                  </label>
                  <span className="text-xs text-[#FF3B5C] font-semibold bg-[#FF3B5C]/10 px-2 py-0.5 rounded-full">
                    Requires 2-4 photos
                  </span>
                </div>

                {/* Active working upload box */}
                <label
                  htmlFor="file-upload"
                  className="flex flex-col items-center justify-center w-full h-36 bg-[var(--bg-container)] border border-dashed border-white/20 rounded-xl cursor-pointer hover:border-white/40 transition-colors"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg
                      className="w-8 h-8 mb-3 text-[var(--text-secondary)]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      ></path>
                    </svg>
                    <p className="mb-1 text-sm text-[var(--text-secondary)]">
                      <span className="font-semibold text-[var(--text-primary)]">
                        Click to upload
                      </span>{" "}
                      images
                    </p>
                    <p className="text-xs text-[var(--text-muted)]">
                      PNG, JPG, or WEBP (Max 4 photos)
                    </p>
                    <p className="text-xs text-[#FFB800]/80 mt-1">
                      For faster uploads, keep each photo under 5MB.
                    </p>
                  </div>
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    accept="image/jpeg, image/png, image/webp"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>

                {/* Dynamic Image Preview Grid */}
                {imagePreviews && imagePreviews.length > 0 && (
                  <div className="grid grid-cols-4 gap-3 mt-2">
                    {imagePreviews.map((url, index) => (
                      <div
                        key={index}
                        className="relative aspect-square w-full rounded-xl overflow-hidden border border-[var(--border-soft)] bg-[var(--bg-container)]"
                      >
                        <img
                          src={url}
                          alt={`Preview ${index}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedFiles((prev: File[]) =>
                              prev.filter((_, i) => i !== index)
                            );
                            setImagePreviews((prev: string[]) =>
                              prev.filter((_, i) => i !== index)
                            );
                          }}
                          className="absolute top-1 right-1 bg-black/70 hover:bg-black text-[var(--text-primary)] text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold transition-colors"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mb-6">
              <p className="text-xs font-bold text-[var(--text-secondary)] mb-3">
                4. SHORT DESCRIPTION <span className="text-[#FF3B5C]">*</span>
              </p>
              <textarea
                ref={descriptionRef}
                value={description}
                onChange={(e) =>
                  setDescription(
                    stripDigits(e.target.value).slice(0, DESCRIPTION_MAX_LENGTH)
                  )
                }
                maxLength={DESCRIPTION_MAX_LENGTH}
                required
                placeholder="Describe what you are selling in your own words..."
                className={`w-full bg-[var(--bg-container)] border border-[var(--border-soft)] rounded-xl p-4 text-sm outline-none focus:border-[#FFB800] h-24 resize-none transition-colors${invalidRing(
                  "description"
                )}`}
              />
              <div className="flex justify-end items-center mt-1.5 px-0.5">
                <span className="text-[10px] text-[var(--text-muted)] shrink-0 ml-2">
                  {description.length}/{DESCRIPTION_MAX_LENGTH}
                </span>
              </div>
            </div>

            <label
              ref={agreedRef}
              tabIndex={-1}
              className={`flex items-start gap-3 mb-2 cursor-pointer group rounded-xl${invalidSectionRing(
                "agreed"
              )}`}
            >
              <div className="mt-0.5 relative shrink-0">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={agreed}
                  onChange={() => setAgreed(!agreed)}
                  required
                />
                <div className="w-5 h-5 border-2 border-[var(--text-muted)] group-hover:border-[var(--text-secondary)] rounded peer-checked:bg-[#FFB800] peer-checked:border-[#FFB800] transition-colors" />
                <CheckCircle2
                  size={14}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[var(--text-primary)] opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none"
                />
              </div>
              <span className="text-xs text-[var(--text-secondary)] leading-relaxed group-hover:text-[var(--text-secondary)] transition-colors">
                I confirm that all uploaded photos are of my own original work
                and were taken by me.
              </span>
            </label>

            {isSubmitting && (
              <div className="flex items-center justify-center gap-2 text-xs text-[var(--text-secondary)] mt-4">
                <svg
                  className="animate-spin h-4 w-4 text-[#FFB800]"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>{uploadProgress || "Uploading your files..."}</span>
              </div>
            )}
          </Step>
        </Stepper>
      </div>
    </div>
  );
}
